import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Alert,
  Avatar,
  Link,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PriceCheckRoundedIcon from "@mui/icons-material/PriceCheckRounded";
import FingerprintRoundedIcon from "@mui/icons-material/FingerprintRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SubscriptionsRoundedIcon from "@mui/icons-material/SubscriptionsRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {
  getPayments,
  getInvoiceLines,
  getVendor,
  getVendorSummary,
  getVendorLogoUrl,
  getInvoiceProofUrl,
} from "../../services/scadentarFurnizoriApi";
import {
  RO_ACCOUNTING_OPTIONS,
  getAccountingLabel,
} from "./accountingPlanRo";
import { getInvoiceIssueDate, sortInvoicesByIssueDate } from "./invoiceIssueDate";

const statusColor = {
  OPEN: "info",
  PARTIAL: "warning",
  PAID: "success",
  CLOSED: "default",
  DRAFT: "default",
  CANCELLED: "error",
};

const STATUS_META = {
  DRAFT: { label: "Draft", color: "#64748b" },
  OPEN: { label: "Open", color: "#0284c7" },
  PARTIAL: { label: "Partial", color: "#d97706" },
  PAID: { label: "Paid", color: "#16a34a" },
  CLOSED: { label: "Closed", color: "#334155" },
  CANCELLED: { label: "Cancelled", color: "#dc2626" },
};

const PAYMENT_METHOD_OPTIONS = [
  { value: "OP", label: "OP" },
  { value: "CARD", label: "Card" },
  { value: "RAMBURS", label: "Ramburs" },
];

function paymentMethodLabel(value) {
  const normalized = String(value || "OP").toUpperCase();
  return PAYMENT_METHOD_OPTIONS.find((item) => item.value === normalized)?.label || "OP";
}

function normalizePaymentMethod(value) {
  const normalized = String(value || "OP").toUpperCase();
  return PAYMENT_METHOD_OPTIONS.some((item) => item.value === normalized) ? normalized : "OP";
}

function paymentMethodClass(value) {
  return `is-${normalizePaymentMethod(value).toLowerCase()}`;
}

function paymentMethodIcon(value) {
  const normalized = normalizePaymentMethod(value);
  if (normalized === "CARD") return <CreditCardRoundedIcon fontSize="small" />;
  if (normalized === "RAMBURS") return <PaidRoundedIcon fontSize="small" />;
  return <ReceiptLongRoundedIcon fontSize="small" />;
}

function formatPaymentDate(value) {
  if (!value) return "-";
  return String(value).slice(0, 10);
}

const PaymentEntryForm = React.memo(function PaymentEntryForm({
  selectedRow,
  onQuickPayment,
  onPatchInvoice,
  setSelectedRow,
  setSelectedPayments,
  setPanelError,
}) {
  const [quickPayDate, setQuickPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [quickPayAmount, setQuickPayAmount] = useState("");
  const [quickPayOp, setQuickPayOp] = useState("");
  const [quickPayMethod, setQuickPayMethod] = useState("OP");
  const [quickPaySaving, setQuickPaySaving] = useState(false);

  useEffect(() => {
    setQuickPayDate(new Date().toISOString().slice(0, 10));
    setQuickPayAmount("");
    setQuickPayOp("");
    setQuickPayMethod("OP");
  }, [selectedRow?.id]);

  const savePayment = async () => {
    if (!selectedRow?.id || !onQuickPayment) return;
    try {
      setQuickPaySaving(true);
      setPanelError("");
      const result = await onQuickPayment(selectedRow, {
        payment_date: quickPayDate,
        amount_ron: Number(quickPayAmount || 0),
        op_number: quickPayOp || null,
        payment_method: quickPayMethod,
      });
      if (result?.invoice) {
        setSelectedRow((prev) => ({ ...prev, ...result.invoice }));
        onPatchInvoice?.(result.invoice);
      }
      const paymentsRes = await getPayments(selectedRow.id);
      setSelectedPayments(Array.isArray(paymentsRes?.data) ? paymentsRes.data : []);
      setQuickPayAmount("");
      setQuickPayOp("");
      setQuickPayMethod("OP");
    } catch (err) {
      setPanelError(
        err?.response?.data?.error ||
        err?.message ||
        "Nu am putut salva plata."
      );
    } finally {
      setQuickPaySaving(false);
    }
  };

  return (
    <Box className="sf-side-pay-form">
      <Box className="sf-side-pay-row sf-side-pay-row-top">
        <TextField
          type="number"
          size="small"
          label="Suma RON"
          value={quickPayAmount}
          onChange={(e) => setQuickPayAmount(e.target.value)}
        />
        <TextField
          size="small"
          select
          label="Mod de plata"
          value={quickPayMethod}
          onChange={(e) => setQuickPayMethod(e.target.value)}
        >
          {PAYMENT_METHOD_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Evidenta plata"
          value={quickPayOp}
          onChange={(e) => setQuickPayOp(e.target.value)}
        />
      </Box>
      <Box className="sf-side-pay-row sf-side-pay-row-bottom">
        <TextField
          type="date"
          size="small"
          label="Data"
          InputLabelProps={{ shrink: true }}
          value={quickPayDate}
          onChange={(e) => setQuickPayDate(e.target.value)}
        />
        <Button
          variant="contained"
          className="sf-btn-primary sf-side-save-btn"
          disabled={quickPaySaving || !Number(quickPayAmount || 0)}
          onClick={savePayment}
        >
          {quickPaySaving ? <CircularProgress size={14} color="inherit" /> : "Salveaza"}
        </Button>
      </Box>
    </Box>
  );
});

function getStatusMeta(status) {
  const key = String(status || "").toUpperCase();
  return STATUS_META[key] || { label: key || "Unknown", color: "#64748b" };
}

function renderStatusOption(status, compact = false) {
  const meta = getStatusMeta(status);
  return (
    <Box className={`sf-status-option ${compact ? "compact" : ""}`}>
      <span className="sf-status-dot" style={{ backgroundColor: meta.color }} />
      <span>{meta.label}</span>
    </Box>
  );
}

function toIsoDate(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function getDatePresetRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (preset === "today") {
    const value = toIsoDate(today);
    return { due_from: value, due_to: value };
  }

  if (preset === "yesterday") {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const value = toIsoDate(yesterday);
    return { due_from: value, due_to: value };
  }

  if (preset === "last_7_days") {
    const from = new Date(today);
    from.setDate(from.getDate() - 6);
    return { due_from: toIsoDate(from), due_to: toIsoDate(today) };
  }

  if (preset === "this_month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { due_from: toIsoDate(from), due_to: toIsoDate(to) };
  }

  if (preset === "last_month") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { due_from: toIsoDate(from), due_to: toIsoDate(to) };
  }

  return { due_from: "", due_to: "" };
}

const DATE_PRESETS = [
  { value: "this_month", label: "Luna curenta" },
  { value: "last_month", label: "Luna trecuta" },
];
const SEARCH_DEBOUNCE_MS = 650;
const MIN_SERVER_SEARCH_CHARS = 2;

function getTipMeta(tip) {
  const value = String(tip || "").toUpperCase();
  if (value === "RECURRENT") {
    return {
      label: "Recurent",
      icon: <AutorenewRoundedIcon fontSize="inherit" />,
      className: "sf-tip-chip sf-tip-recurrent",
    };
  }
  return {
    label: "One-time",
    icon: <BoltRoundedIcon fontSize="inherit" />,
    className: "sf-tip-chip sf-tip-one-time",
  };
}

const availableStatuses = [
  "DRAFT",
  "OPEN",
  "PARTIAL",
  "PAID",
  "CLOSED",
  "CANCELLED",
];

function parseContTags(value) {
  return String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function hasInvoiceProof(row) {
  return Boolean(
    row?.doc_factura_url || row?.invoice_doc_url || row?.document_url || row?.doc_plata_url
  );
}

function formatAuditDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ro-RO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}


function formatDueDatePretty(value) {
  if (!value || value === "-") return "-";
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function buildManualStatusMeta(fromStatus, toStatus) {
  return `${fromStatus || "-"} -> ${toStatus || "-"}`;
}

function buildEditInvoiceFromRow(row) {
  if (!row) return null;
  return {
    tip_plata: row.tip_plata || "ONE_TIME",
    numar: row.numar || "",
    factura: row.factura || row.numar || "",
    serviciu: row.serviciu || "",
    cont_contabil_tags: parseContTags(row.cont_contabil),
    luna: row.luna || (row.data_emitere ? String(row.data_emitere).slice(0, 7) : ""),
    data_emitere: row.data_emitere ? String(row.data_emitere).slice(0, 10) : "",
    termen_scadent: row.termen_scadent ? String(row.termen_scadent).slice(0, 10) : "",
    saptamana_scadenta: row.saptamana_scadenta || "",
    valuta: row.valuta || "RON",
    suma_valuta: row.suma_valuta ?? "",
    transmisa_in_spv: Number(row.transmisa_in_spv || 0) === 1,
  };
}

function OverviewTab({
  invoices,
  selectedInvoiceId,
  onSelectInvoice,
  month,
  onMonthChange,
  onClearMonth,
  filters,
  onFilterChange,
  onRefresh,
  onRefreshQuiet,
  onOpenVendor,
  onOpenSpvDocument,
  onQuickPayment,
  onUpload,
  onDownloadInvoicesZip,
  isDownloadingInvoicesZip,
  onValidateInvoicePdf,
  onGenerateFromSpv,
  onSaveInvoice,
  onSetManualStatus,
  onGetStatusAudit,
  onDeleteInvoice,
  onDeleteInvoicesBulk,
  onPatchInvoice,
  onPatchVendorInvoices,
  onPreviewFx,
  isRefreshing = false,
}) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedVendorSummary, setSelectedVendorSummary] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectedInvoiceLines, setSelectedInvoiceLines] = useState([]);
  const [panelError, setPanelError] = useState("");
  const [panelInfo, setPanelInfo] = useState("");
  const [invoiceDocFile, setInvoiceDocFile] = useState(null);
  const [invoiceDropActive, setInvoiceDropActive] = useState(false);
  const [invoiceDocSaving, setInvoiceDocSaving] = useState(false);
  const [invoiceDocError, setInvoiceDocError] = useState("");
  const [invoiceDocWarning, setInvoiceDocWarning] = useState("");
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [statusAudit, setStatusAudit] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingStatusRow, setPendingStatusRow] = useState(null);
  const [pendingStatusReason, setPendingStatusReason] = useState("");
  const [manualStatusSaving, setManualStatusSaving] = useState(false);
  const [statusActionMessage, setStatusActionMessage] = useState(null);
  const [invoiceDeleting, setInvoiceDeleting] = useState(false);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkSelectionError, setBulkSelectionError] = useState("");
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [invoiceEditMode, setInvoiceEditMode] = useState(false);
  const [fxPreview, setFxPreview] = useState({ sumaRon: 0, cursBnr: null, cursDate: null });
  const [fxPreviewLoading, setFxPreviewLoading] = useState(false);
  const [searchText, setSearchText] = useState(String(filters?.q || ""));
  const [issueDateOrder, setIssueDateOrder] = useState("desc");
  const invoiceDocInputRef = useRef(null);
  const downloadMenuOpen = Boolean(downloadMenuAnchor);

  const runInvoicesDownload = (options = {}) => {
    setDownloadMenuAnchor(null);
    onDownloadInvoicesZip?.(options);
  };

  const applyInvoiceDocCandidate = (file) => {
    setInvoiceDocError("");
    setInvoiceDocWarning("");
    if (!file) {
      setInvoiceDocFile(null);
      return;
    }
    const ok = file.type === "application/pdf" || file.type.startsWith("image/");
    if (!ok) {
      setInvoiceDocFile(null);
      setInvoiceDocError("Acceptat doar PDF sau imagini.");
      return;
    }
    setInvoiceDocFile(file);
  };

  useEffect(() => {
    if (!selectedRow?.id) return;
    let cancelled = false;
    setPanelError("");
    setPanelInfo("");

    (async () => {
      try {
        const requests = [getPayments(selectedRow.id), getInvoiceLines(selectedRow.id)];
        if (selectedRow.vendor_id) {
          requests.push(getVendor(selectedRow.vendor_id), getVendorSummary(selectedRow.vendor_id));
        }
        const [paymentsRes, linesRes, vendorRes, vendorSummaryRes] = await Promise.all(requests);
        if (cancelled) return;
        setSelectedPayments(Array.isArray(paymentsRes?.data) ? paymentsRes.data : []);
        setSelectedInvoiceLines(Array.isArray(linesRes?.data) ? linesRes.data : []);
        setSelectedVendor(vendorRes?.data || null);
        setSelectedVendorSummary(vendorSummaryRes?.data || null);
      } catch (err) {
        if (cancelled) return;
        setPanelError(err?.response?.data?.error || err.message || "Nu am putut incarca detaliile.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRow]);

  useEffect(() => {
    setSearchText(String(filters?.q || ""));
  }, [filters?.q]);

  useEffect(() => {
    const rawSearch = String(searchText || "").trim();
    const nextSearch = rawSearch.length >= MIN_SERVER_SEARCH_CHARS ? rawSearch : "";
    const currentSearch = String(filters?.q || "").trim();
    if (nextSearch === currentSearch) return undefined;

    const timer = setTimeout(() => {
      onFilterChange("q", nextSearch);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [filters?.q, onFilterChange, searchText]);

  useEffect(() => {
    if (!selectedInvoiceId) return;
    const rowFromList = (invoices || []).find(
      (row) => Number(row?.id) === Number(selectedInvoiceId)
    );
    if (rowFromList) {
      setSelectedRow((prev) => ({ ...(prev || {}), ...rowFromList }));
    }
  }, [selectedInvoiceId, invoices]);

  useEffect(() => {
    if (!selectedRow?.id) return;
    const latest = (invoices || []).find(
      (row) => Number(row?.id) === Number(selectedRow.id)
    );
    if (latest) {
      setSelectedRow((prev) => ({ ...prev, ...latest }));
    }
  }, [invoices, selectedRow?.id]);

  useEffect(() => {
    if (!selectedRow?.id) return;
    let cancelled = false;
    const run = async () => {
      if (!onGetStatusAudit) {
        setStatusAudit([]);
        return;
      }
      try {
        const rows = await onGetStatusAudit(selectedRow, 50);
        if (!cancelled) setStatusAudit(Array.isArray(rows) ? rows : []);
      } catch (_) {
        if (!cancelled) setStatusAudit([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedRow, onGetStatusAudit]);

  useEffect(() => {
    if (!selectedRow?.id) return;
    setSelectedInvoiceLines([]);
    setInvoiceDocFile(null);
    setInvoiceDocError("");
    setInvoiceDocWarning("");
    setInvoiceEditMode(false);
    setEditInvoice(buildEditInvoiceFromRow(selectedRow));
    // Reset only when changing invoice; list refreshes should not overwrite an edit in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow?.id]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!editInvoice) return;
      const valuta = String(editInvoice.valuta || "RON").toUpperCase();
      const sumaValuta = Number(editInvoice.suma_valuta || 0);
      const dataEmitere = editInvoice.data_emitere;
      if (valuta === "RON") {
        setFxPreview({ sumaRon: sumaValuta, cursBnr: null, cursDate: null });
        return;
      }
      if (!onPreviewFx || !sumaValuta || !dataEmitere) {
        setFxPreview({ sumaRon: 0, cursBnr: null, cursDate: null });
        return;
      }
      try {
        setFxPreviewLoading(true);
        const data = await onPreviewFx({
          valuta,
          suma_valuta: sumaValuta,
          data_emitere: dataEmitere,
        });
        if (!cancelled) setFxPreview(data || { sumaRon: 0, cursBnr: null, cursDate: null });
      } catch (_) {
        if (!cancelled) setFxPreview({ sumaRon: 0, cursBnr: null, cursDate: null });
      } finally {
        if (!cancelled) setFxPreviewLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [editInvoice, onPreviewFx]);

  const vendorDisplay = useMemo(() => {
    if (!selectedRow) return null;
    return {
      id: selectedRow.vendor_id || selectedVendor?.id || null,
      denumire:
        selectedVendor?.denumire ||
        selectedRow.vendor_name ||
        selectedRow.denumire ||
        "Furnizor",
      cod_fiscal:
        selectedVendor?.cod_fiscal ||
        selectedRow.supplier_cui ||
        "-",
      email: selectedVendor?.email || "-",
      telefon: selectedVendor?.telefon || "-",
      oras:
        selectedVendor?.oras ||
        selectedVendor?.localitate ||
        selectedVendor?.city ||
        "-",
      vendor_scope: selectedVendor?.vendor_scope || null,
    };
  }, [selectedRow, selectedVendor]);

  const customerTypeInfo = useMemo(() => {
    const isSpv = String(selectedRow?.source || "").toUpperCase() === "SPV";
    const scope = String(vendorDisplay?.vendor_scope || "").toUpperCase();
    if (isSpv) {
      return { key: "SPV_ANAF", label: "SPV ANAF", editable: false };
    }
    if (scope === "INTRA_EU") return { key: "INTRA_EU", label: "Intracomunitar", editable: true };
    if (scope === "NON_EU") return { key: "NON_EU", label: "Extracomunitar", editable: true };
    return { key: "INTRA_EU", label: "Intracomunitar", editable: true };
  }, [selectedRow?.source, vendorDisplay?.vendor_scope]);


  const invoiceProofUrl = useMemo(() => {
    return (
      selectedRow?.doc_factura_url ||
      selectedRow?.invoice_doc_url ||
      selectedRow?.document_url ||
      selectedRow?.doc_plata_url ||
      null
    );
  }, [selectedRow]);

  const invoiceProofProxyUrl = useMemo(() => {
    if (!selectedRow?.id || !invoiceProofUrl) return null;
    return getInvoiceProofUrl(selectedRow.id);
  }, [selectedRow?.id, invoiceProofUrl]);

  const isSpvSource = String(selectedRow?.source || "").toUpperCase() === "SPV";

  const unpaidInvoicesCount = useMemo(() => {
    const fromSummary =
      selectedVendorSummary?.open_invoices_count ??
      selectedVendorSummary?.unpaid_invoices_count ??
      selectedVendorSummary?.active_invoices_count ??
      null;
    if (fromSummary !== null && fromSummary !== undefined) return Number(fromSummary);
    const currentVendorId = Number(vendorDisplay?.id || 0);
    if (!currentVendorId) return 0;
    return (invoices || []).filter((x) =>
      Number(x?.vendor_id || 0) === currentVendorId &&
      ["OPEN", "PARTIAL"].includes(String(x?.status || "").toUpperCase())
    ).length;
  }, [selectedVendorSummary, vendorDisplay?.id, invoices]);

  const extraInvoice = useMemo(() => {
    if (!selectedRow) return null;
    return {
      contContabil: selectedRow.cont_contabil || "-",
      lunaEmitere:
        selectedRow.luna ||
        (selectedRow.data_emitere ? String(selectedRow.data_emitere).slice(0, 7) : "-"),
      moneda: selectedRow.valuta || "RON",
      sumaValuta: Number(selectedRow.suma_valuta || 0).toFixed(2),
      cursBnr: selectedRow.curs_bnr ? Number(selectedRow.curs_bnr).toFixed(6) : null,
      cursDate: selectedRow.curs_date || null,
      scadenta: selectedRow.termen_scadent?.slice(0, 10) || "-",
      saptamanaScadenta: selectedRow.saptamana_scadenta || "-",
      transmisaSpv: Number(selectedRow.transmisa_in_spv || 0) === 1,
    };
  }, [selectedRow]);

  const foreignCurrencyInfo = useMemo(() => {
    if (!selectedRow) return null;
    const currency = String(selectedRow.valuta || "RON").toUpperCase();
    if (!["EUR", "USD"].includes(currency)) return null;

    const rate = Number(selectedRow.curs_bnr || 0);
    const totalForeign = Number(selectedRow.suma_valuta || 0);
    const storedRestForeign = Number(selectedRow.rest_plata_valuta || 0);
    const restRon = Number(selectedRow.rest_plata_ron || 0);
    const restForeign = storedRestForeign || (rate > 0 ? restRon / rate : 0);

    const formatForeign = (value) => `${Number(value || 0).toFixed(2)} ${currency}`;
    const formatRate = () =>
      rate > 0
        ? `Curs BNR: ${rate.toFixed(6)}${selectedRow.curs_date ? ` (${String(selectedRow.curs_date).slice(0, 10)})` : ""}`
        : "Curs BNR: -";

    return {
      currency,
      totalForeign: formatForeign(totalForeign),
      restForeign: formatForeign(restForeign),
      rateLabel: formatRate(),
    };
  }, [selectedRow]);


  const dueInfo = useMemo(() => {
    const due = extraInvoice?.scadenta;
    if (!due || due === "-") {
      return { dateLabel: "-", deltaLabel: "fara scadenta", color: "#64748b" };
    }

    const dueDate = new Date(`${due}T00:00:00`);
    if (Number.isNaN(dueDate.getTime())) {
      return { dateLabel: formatDueDatePretty(due), deltaLabel: "data invalida", color: "#64748b" };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.round((dueDate.getTime() - today.getTime()) / 86400000);

    if (diffDays > 0) {
      return { dateLabel: formatDueDatePretty(due), deltaLabel: `${diffDays} zile ramase`, color: "#16a34a" };
    }
    if (diffDays === 0) {
      return { dateLabel: formatDueDatePretty(due), deltaLabel: "scadenta azi", color: "#16a34a" };
    }
    return {
      dateLabel: formatDueDatePretty(due),
      deltaLabel: `depasita cu ${Math.abs(diffDays)} zile`,
      color: "#dc2626",
    };
  }, [extraInvoice?.scadenta]);

  const statusCounts = useMemo(() => {
    const counts = {
      ALL: (invoices || []).length,
    };
    availableStatuses.forEach((st) => {
      counts[st] = (invoices || []).filter(
        (row) => String(row?.status || "").toUpperCase() === st
      ).length;
    });
    return counts;
  }, [invoices]);

  const statusFilterOptions = useMemo(
    () => [
      { value: "", label: "All", count: statusCounts.ALL || 0, tone: "all" },
      ...availableStatuses.map((st) => ({
        value: st,
        label: getStatusMeta(st).label,
        count: statusCounts[st] || 0,
        tone: String(st || "").toLowerCase(),
      })),
    ],
    [statusCounts]
  );

  const filteredInvoices = useMemo(() => {
    const activeStatus = String(filters?.status || "").toUpperCase();
    const q = String(searchText || "").trim().toLowerCase();

    const rows = (invoices || []).filter((row) => {
      const rowStatus = String(row?.status || "").toUpperCase();

      const haystack = [
        row?.vendor_name,
        row?.denumire,
        row?.numar,
        row?.factura,
        row?.serviciu,
        row?.info,
        row?.supplier_cui,
        row?.vendor_cod_fiscal,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");

      if (q) {
        // Cand cautam, ignoram status/date presets si afisam orice match.
        return haystack.includes(q);
      }

      return activeStatus ? rowStatus === activeStatus : true;
    });

    return sortInvoicesByIssueDate(rows, issueDateOrder);
  }, [invoices, filters?.status, searchText, issueDateOrder]);

  const visibleBulkIds = useMemo(
    () =>
      filteredInvoices
        .map((row) => String(row?.id || ""))
        .filter(Boolean),
    [filteredInvoices]
  );
  const selectedBulkRows = useMemo(
    () =>
      filteredInvoices.filter((row) =>
        bulkSelectedIds.has(String(row?.id || ""))
      ),
    [bulkSelectedIds, filteredInvoices]
  );
  const bulkSelectedCount = selectedBulkRows.length;
  const allVisibleSelected =
    visibleBulkIds.length > 0 && visibleBulkIds.every((id) => bulkSelectedIds.has(id));
  const someVisibleSelected =
    visibleBulkIds.some((id) => bulkSelectedIds.has(id)) && !allVisibleSelected;

  const openBulkSelection = () => {
    setBulkSelectionError("");
    setBulkSelectedIds(new Set());
    setBulkSelectMode(true);
    setSelectedRow(null);
    onSelectInvoice?.(null);
  };

  const closeBulkSelection = () => {
    setBulkSelectMode(false);
    setBulkSelectedIds(new Set());
    setBulkSelectionError("");
  };

  const openManualStatusConfirm = (row, nextStatus, reason) => {
    const normalizedStatus = String(nextStatus || "").toUpperCase();
    const currentStatus = String(row?.status || "").toUpperCase();
    if (!row?.id || !normalizedStatus || normalizedStatus === currentStatus) return;
    setStatusActionMessage(null);
    setPendingStatus(normalizedStatus);
    setPendingStatusRow(row);
    setPendingStatusReason(reason || "Manual override din tabel");
    setStatusDialogOpen(true);
  };

  const closeManualStatusConfirm = () => {
    if (manualStatusSaving) return;
    setStatusDialogOpen(false);
    setPendingStatus("");
    setPendingStatusRow(null);
    setPendingStatusReason("");
  };

  const toggleBulkInvoiceSelection = (invoiceId) => {
    const key = String(invoiceId || "");
    if (!key) return;
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleVisibleBulkSelection = () => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleBulkIds.forEach((id) => next.delete(id));
      } else {
        visibleBulkIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDeleteInvoices = async () => {
    if (!selectedBulkRows.length) return;
    const label =
      selectedBulkRows.length === 1
        ? `factura ${selectedBulkRows[0]?.numar || selectedBulkRows[0]?.factura || selectedBulkRows[0]?.id}`
        : `${selectedBulkRows.length} facturi`;
    if (!window.confirm(`Stergi ${label} selectate?`)) return;

    try {
      setBulkDeleting(true);
      setBulkSelectionError("");
      if (onDeleteInvoicesBulk) {
        await onDeleteInvoicesBulk(selectedBulkRows);
      } else if (onDeleteInvoice) {
        for (const row of selectedBulkRows) {
          // Fallback pentru cazul in care parintele nu expune bulk delete.
          // eslint-disable-next-line no-await-in-loop
          await onDeleteInvoice(row);
        }
      }
      closeBulkSelection();
    } catch (err) {
      setBulkSelectionError(
        err?.response?.data?.error || err?.message || "Nu am putut sterge facturile selectate."
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const normalizedSearchText = String(searchText || "").trim();
  const serverSearchText =
    normalizedSearchText.length >= MIN_SERVER_SEARCH_CHARS ? normalizedSearchText : "";
  const isSearchPending = serverSearchText !== String(filters?.q || "").trim();
  const showSearchActivity = Boolean(isRefreshing || isSearchPending);

  const statusConfirmDialog = (
    <Dialog open={statusDialogOpen} onClose={closeManualStatusConfirm} maxWidth="xs" fullWidth>
      <DialogTitle>Suprascriere reguli business</DialogTitle>
      <DialogContent>
        <Typography>
          Doresti sa suprascrii regulile de business si sa setezi manual statusul facturii{" "}
          <strong>{pendingStatusRow?.numar || pendingStatusRow?.factura || pendingStatusRow?.id || "-"}</strong>{" "}
          la <strong>{pendingStatus || "-"}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeManualStatusConfirm} disabled={manualStatusSaving}>Nu</Button>
        <Button
          variant="contained"
          disabled={manualStatusSaving || !pendingStatus}
          onClick={async () => {
            const targetRow = pendingStatusRow || selectedRow;
            if (!onSetManualStatus || !targetRow?.id || !pendingStatus) {
              closeManualStatusConfirm();
              return;
            }
            try {
              setManualStatusSaving(true);
              setPanelError("");
              const result = await onSetManualStatus(targetRow, {
                status: pendingStatus,
                reason: pendingStatusReason || "Manual override din tabel",
                override_business_rules: true,
              });
              const savedInvoice = result?.invoice || null;
              const savedAudit = result?.audit || null;
              if (savedInvoice) {
                setSelectedRow((prev) =>
                  Number(prev?.id) === Number(savedInvoice.id) ? { ...prev, ...savedInvoice } : prev
                );
                onPatchInvoice?.(savedInvoice);
              }
              if (savedAudit && Number(selectedRow?.id) === Number(targetRow.id)) {
                setStatusAudit((prev) => [savedAudit, ...(prev || [])]);
              } else if (onGetStatusAudit && Number(selectedRow?.id) === Number(targetRow.id)) {
                const rows = await onGetStatusAudit(targetRow, 50);
                setStatusAudit(Array.isArray(rows) ? rows : []);
              }
              setPanelInfo("Status actualizat manual.");
              setStatusActionMessage({
                severity: "success",
                message: "Status actualizat manual.",
              });
              closeManualStatusConfirm();
            } catch (err) {
              const message =
                err?.response?.data?.error || err.message || "Nu am putut seta statusul manual.";
              setPanelError(message);
              setStatusActionMessage({
                severity: "error",
                message,
              });
            } finally {
              setManualStatusSaving(false);
            }
          }}
        >
          {manualStatusSaving ? <CircularProgress size={16} color="inherit" /> : "Da, seteaza manual"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box className={`sf-overview-split ${selectedRow ? "has-panel" : ""}`}>
      <Box className="sf-overview-main">
        <Box className="sf-overview-toolbar">
          <Box className="sf-status-filter-bar">
            {statusFilterOptions.map((item) => {
              const active = String(filters.status || "") === String(item.value || "");
              return (
                <Button
                  key={`status-pill-${item.value || "ALL"}`}
                  className={`sf-status-filter-pill sf-status-filter-pill-${item.tone || "all"} ${active ? "is-active" : ""}`}
                  onClick={() => onFilterChange("status", item.value)}
                >
                  <span className="sf-status-filter-label">{item.label}</span>
                  <span className="sf-status-filter-count">{item.count}</span>
                </Button>
              );
            })}
          </Box>

          <Box className="sf-overview-control-group">
            <TextField
              size="small"
              label="Cauta"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="sf-overview-search-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: showSearchActivity ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
            <TextField
              size="small"
              select
              label="Moneda"
              value={filters.currency}
              onChange={(e) => onFilterChange("currency", e.target.value)}
              className="sf-overview-currency-field"
            >
              <MenuItem value="">Toate</MenuItem>
              <MenuItem value="RON">RON</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={onRefresh}
              className="sf-overview-refresh-btn"
              startIcon={<RefreshRoundedIcon fontSize="small" />}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Box className="sf-date-presets-row">
          <Box className="sf-date-manual-wrap">
            <TextField
              type="date"
              size="small"
              label="De la"
              InputLabelProps={{ shrink: true }}
              value={filters.due_from || ""}
              onChange={(e) => {
                const nextFrom = e.target.value;
                onFilterChange("date_preset", "");
                onFilterChange("due_from", nextFrom);
                if (nextFrom && filters.due_to && filters.due_to < nextFrom) {
                  onFilterChange("due_to", nextFrom);
                }
              }}
            />
            <span className="sf-date-range-divider" aria-hidden="true" />
            <TextField
              type="date"
              size="small"
              label="Pana la"
              InputLabelProps={{ shrink: true }}
              value={filters.due_to || ""}
              inputProps={{ min: filters.due_from || undefined }}
              onChange={(e) => {
                const nextTo = e.target.value;
                onFilterChange("date_preset", "");
                onFilterChange(
                  "due_to",
                  filters.due_from && nextTo && nextTo < filters.due_from
                    ? filters.due_from
                    : nextTo
                );
              }}
            />
          </Box>
          <Box className="sf-month-filter-wrap">
            <TextField
              type="month"
              size="small"
              value={month || ""}
              onChange={(e) => onMonthChange?.(e.target.value)}
              className="sf-month-filter-field"
            />
            <Button
              variant="outlined"
              className="sf-month-clear-btn"
              onClick={() => onClearMonth?.()}
            >
              Toate lunile
            </Button>
          </Box>
          <Stack className="sf-date-actions" direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {DATE_PRESETS.map((preset) => (
              <Chip
                key={preset.value}
                clickable
                label={preset.label}
                onClick={() => {
                  const selected = filters.date_preset === preset.value ? "" : preset.value;
                  const range = getDatePresetRange(selected);
                  onFilterChange("date_preset", selected);
                  onFilterChange("due_from", range.due_from);
                  onFilterChange("due_to", range.due_to);
                }}
                className={`sf-date-chip ${
                  filters.date_preset === preset.value ? "is-active" : ""
                }`}
              />
            ))}
            <Chip
              clickable
              label="Reset"
              onClick={() => {
                onFilterChange("date_preset", "");
                onFilterChange("due_from", "");
                onFilterChange("due_to", "");
              }}
              className="sf-date-chip sf-date-chip-reset"
            />
            <Button
              variant="outlined"
              size="small"
              className="sf-download-invoices-btn"
              startIcon={
                isDownloadingInvoicesZip ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <DownloadRoundedIcon fontSize="small" />
                )
              }
              endIcon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
              disabled={isDownloadingInvoicesZip || !onDownloadInvoicesZip}
              onClick={(event) => setDownloadMenuAnchor(event.currentTarget)}
            >
              Download facturi
            </Button>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={downloadMenuOpen}
              onClose={() => setDownloadMenuAnchor(null)}
              className="sf-download-invoices-menu"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                className="sf-download-invoices-menu-item"
                onClick={() => runInvoicesDownload({ excludeSpv: false })}
              >
                <ListItemIcon>
                  <DownloadRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Descarca tot"
                  secondary="Include toate facturile din perioada selectata."
                />
              </MenuItem>
              <MenuItem
                className="sf-download-invoices-menu-item"
                onClick={() => runInvoicesDownload({ excludeSpv: true })}
              >
                <ListItemIcon>
                  <SubscriptionsRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Descarca Subscriptii"
                  secondary="Exclude facturile descarcate din SPV."
                />
              </MenuItem>
            </Menu>
          </Stack>
      </Box>

      <Paper className="sf-table-shell sf-overview-table sf-order-card">
        <Box className="sf-order-card-head">
          <Typography className="sf-order-card-title">Details</Typography>
          <Box className="sf-order-head-actions">
            {bulkSelectMode ? (
              <Box className="sf-bulk-selection-toolbar">
                <Typography className="sf-bulk-selection-count">
                  {bulkSelectedCount} selectate
                </Typography>
                <Tooltip title="Sterge facturile selectate">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      className="sf-bulk-delete-btn"
                      startIcon={
                        bulkDeleting ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <DeleteRoundedIcon fontSize="small" />
                        )
                      }
                      disabled={bulkDeleting || !bulkSelectedCount || (!onDeleteInvoicesBulk && !onDeleteInvoice)}
                      onClick={handleBulkDeleteInvoices}
                    >
                      Sterge
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Inchide selectia">
                  <IconButton
                    size="small"
                    className="sf-bulk-close-btn"
                    disabled={bulkDeleting}
                    onClick={closeBulkSelection}
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                <Tooltip title="Selecteaza facturi pentru stergere">
                  <span>
                    <IconButton
                      size="small"
                      className="sf-bulk-select-trigger"
                      disabled={!filteredInvoices.length || (!onDeleteInvoicesBulk && !onDeleteInvoice)}
                      onClick={openBulkSelection}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton size="small" className="sf-order-edit-btn">
                  <EditRoundedIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
          {bulkSelectionError ? (
            <Alert severity="error" className="sf-bulk-selection-alert">
              {bulkSelectionError}
            </Alert>
          ) : null}
          {statusActionMessage ? (
            <Alert
              severity={statusActionMessage.severity || "info"}
              className="sf-bulk-selection-alert"
              onClose={() => setStatusActionMessage(null)}
            >
              {statusActionMessage.message}
            </Alert>
          ) : null}
          {isRefreshing ? (
            <Box className="sf-overview-inline-refresh">
              <CircularProgress size={15} />
              <span>Se cauta...</span>
            </Box>
          ) : null}
          <Table size="small" stickyHeader className="sf-details-table">
          <TableHead>
            <TableRow>
              {bulkSelectMode ? (
                <TableCell padding="checkbox" className="sf-bulk-select-cell">
                  <Checkbox
                    size="small"
                    color="error"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    disabled={!visibleBulkIds.length || bulkDeleting}
                    onClick={(event) => event.stopPropagation()}
                    onChange={toggleVisibleBulkSelection}
                  />
                </TableCell>
              ) : null}
              <TableCell>Furnizor</TableCell>
              <TableCell>Factura</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell sortDirection={issueDateOrder} sx={{ whiteSpace: "nowrap" }}>
                <TableSortLabel
                  active
                  direction={issueDateOrder}
                  onClick={() => setIssueDateOrder((order) => order === "desc" ? "asc" : "desc")}
                >
                  Data emiterii
                </TableSortLabel>
              </TableCell>
              <TableCell>Scadenta</TableCell>
              <TableCell>Valoare RON</TableCell>
              <TableCell>Rest RON</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInvoices.map((row) => {
              const tipMeta = getTipMeta(row.tip_plata);
              const dueDateRaw = String(row?.termen_scadent || "").slice(0, 10);
              const dueDateObj = /^\d{4}-\d{2}-\d{2}$/.test(dueDateRaw)
                ? new Date(`${dueDateRaw}T00:00:00`)
                : null;
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              const overdueDays =
                dueDateObj && !Number.isNaN(dueDateObj.getTime()) && dueDateObj < today
                  ? Math.round((today.getTime() - dueDateObj.getTime()) / 86400000)
                  : 0;
              const isPriorityOverdue = overdueDays >= 30 && ["OPEN", "PARTIAL", "DRAFT"].includes(String(row?.status || "").toUpperCase());
              return (
              <TableRow
                key={row.id}
                hover
                onClick={() => {
                  if (bulkSelectMode) {
                    toggleBulkInvoiceSelection(row.id);
                    return;
                  }
                  setSelectedRow(row);
                  onSelectInvoice?.(row.id);
                }}
                className={`sf-clickable-row ${bulkSelectMode ? "sf-bulk-select-row" : ""} ${selectedRow?.id === row.id ? "is-active" : ""} ${isPriorityOverdue ? "sf-overdue-priority" : ""}`}
              >
                {bulkSelectMode ? (
                  <TableCell padding="checkbox" className="sf-bulk-select-cell">
                    <Checkbox
                      size="small"
                      color="error"
                      checked={bulkSelectedIds.has(String(row?.id || ""))}
                      disabled={bulkDeleting}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleBulkInvoiceSelection(row.id)}
                    />
                  </TableCell>
                ) : null}
                <TableCell>
                  <Box className="sf-row-main-cell">
                    <Avatar
                      src={row.vendor_id ? getVendorLogoUrl(row.vendor_id) : undefined}
                      className="sf-row-avatar"
                    >
                      {String(row.vendor_name || row.denumire || "?").charAt(0)}
                    </Avatar>
                    <Box>
                      {onOpenVendor ? (
                        <Link
                          component="button"
                          underline="hover"
                          className="sf-vendor-link"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenVendor?.(row);
                          }}
                        >
                          {row.vendor_name || row.denumire}
                        </Link>
                      ) : (
                        <Typography className="sf-cell-primary">{row.denumire}</Typography>
                      )}
                      <Typography className="sf-cell-secondary">{row.serviciu}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{row.numar || "-"}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      icon={tipMeta.icon}
                      label={tipMeta.label}
                      className={tipMeta.className}
                    />
                    {Number(row?.is_prediction || 0) === 1 ? (
                      <Chip
                        size="small"
                        label="PREDICTIE"
                        sx={{
                          bgcolor: "#fef3c7",
                          color: "#92400e",
                          border: "1px solid #f59e0b",
                          fontWeight: 800,
                        }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {getInvoiceIssueDate(row.data_emitere) || "-"}
                </TableCell>
                <TableCell>
                  <Stack spacing={0.35}>
                    <Typography variant="body2">{row.termen_scadent?.slice(0, 10) || "-"}</Typography>
                    {isPriorityOverdue ? (
                      <Typography variant="caption" sx={{ color: "#b91c1c", fontWeight: 800 }}>
                        Restanta veche: {overdueDays} zile
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{Number(row.suma_ron || 0).toFixed(2)}</TableCell>
                <TableCell>{Number(row.rest_plata_ron || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Box
                    className="sf-table-status-cell"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <TextField
                      size="small"
                      select
                      className="sf-table-status-select"
                      value={String(row.status || "OPEN").toUpperCase()}
                      disabled={manualStatusSaving}
                      SelectProps={{
                        renderValue: (value) => renderStatusOption(value, true),
                        MenuProps: {
                          className: "sf-status-select-menu",
                        },
                      }}
                      onChange={(event) =>
                        openManualStatusConfirm(row, event.target.value, "Manual override din tabel")
                      }
                    >
                      {availableStatuses.map((st) => (
                        <MenuItem key={st} value={st}>
                          {renderStatusOption(st)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const hasAttachedPdf = hasInvoiceProof(row);
                    const hasSpvPdf = String(row.source || "").toUpperCase() === "SPV" && Boolean(row.external_id);
                    const canOpenPdf = Boolean(hasAttachedPdf || (onOpenSpvDocument && hasSpvPdf));
                    const tooltipTitle = hasAttachedPdf
                      ? "Factura PDF atasata"
                      : hasSpvPdf
                        ? "PDF SPV disponibil"
                        : "Fara PDF atasat";

                    return (
                      <Tooltip title={tooltipTitle}>
                        <span>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hasAttachedPdf && row.id) {
                                window.open(getInvoiceProofUrl(row.id), "_blank", "noopener,noreferrer");
                                return;
                              }
                              if (hasSpvPdf) onOpenSpvDocument?.(row);
                            }}
                            size="small"
                            className={`sf-table-pdf-action ${hasAttachedPdf ? "has-pdf" : "no-pdf"}`}
                            disabled={!canOpenPdf}
                          >
                            <PictureAsPdfRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
              </TableRow>
              );
            })}
            {!filteredInvoices.length && (
              <TableRow>
                <TableCell colSpan={bulkSelectMode ? 10 : 9} align="center">
                  Nu exista date pentru filtrele curente.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      </Box>

      {selectedRow && (
        <Box className="sf-overview-panel-slot">
          <Paper className="sf-overview-panel">
          <Box className="sf-overview-panel-head">
            <TextField
              size="small"
              select
              label="Status"
              className="sf-manual-status-select"
              value={String(selectedRow?.status || "OPEN").toUpperCase()}
              SelectProps={{
                renderValue: (value) => renderStatusOption(value, true),
              }}
              onChange={(e) => {
                const nextStatus = String(e.target.value || "").toUpperCase();
                openManualStatusConfirm(selectedRow, nextStatus, "Manual override din side panel");
              }}
            >
              {availableStatuses.map((st) => (
                <MenuItem key={st} value={st}>
                  {renderStatusOption(st)}
                </MenuItem>
              ))}
            </TextField>
            <Box className="sf-panel-head-actions">
              <Tooltip title={invoiceProofUrl ? "Print factura atasata" : "Nu exista factura atasata"}>
                <span>
                  <IconButton
                    size="small"
                    className="sf-panel-head-icon"
                    disabled={!invoiceProofUrl}
                    onClick={() => {
                      if (!invoiceProofProxyUrl) return;
                      const w = window.open(invoiceProofProxyUrl, "_blank", "noopener,noreferrer");
                      if (w) {
                        setTimeout(() => {
                          try {
                            w.focus();
                            w.print();
                          } catch (_) {}
                        }, 500);
                      }
                    }}
                  >
                    <PrintRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                size="small"
                className="sf-panel-head-icon"
                onClick={() => {
                  setSelectedRow(null);
                  onSelectInvoice?.(null);
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box className="sf-side-section sf-side-card sf-right-card sf-customer-card sf-customer-card-v2">
            <Box className="sf-customer-top-v2">
              <Avatar
                src={vendorDisplay?.id ? getVendorLogoUrl(vendorDisplay.id) : undefined}
                className="sf-customer-avatar sf-customer-avatar-v2"
              >
                {(vendorDisplay?.denumire || "?")[0]}
              </Avatar>

              <Box className="sf-customer-identity sf-customer-identity-v2">
                {onOpenVendor ? (
                  <Link
                    component="button"
                    underline="none"
                    className="sf-vendor-link sf-vendor-link-v2 sf-vendor-link-inline-v2"
                    onClick={() =>
                      onOpenVendor?.({
                        ...(selectedRow || {}),
                        vendor_id: vendorDisplay?.id || selectedRow?.vendor_id,
                        id: vendorDisplay?.id || selectedRow?.vendor_id || selectedRow?.id,
                        sourceInvoiceId: selectedRow?.id,
                        denumire: vendorDisplay?.denumire || selectedRow?.denumire,
                      })
                    }
                  >
                    {vendorDisplay?.denumire}
                  </Link>
                ) : (
                  <Typography className="sf-cell-primary sf-customer-name sf-customer-name-v2 sf-customer-name-inline-v2">
                    {vendorDisplay?.denumire}
                  </Typography>
                )}

                <Typography className="sf-customer-contact-line-v2">
                  <span><strong>Email:</strong> {vendorDisplay?.email && vendorDisplay?.email !== "-" ? vendorDisplay?.email : "-"}</span>
                  <span className="sf-contact-sep" aria-hidden="true">|</span>
                  <span><strong>Telefon:</strong> {vendorDisplay?.telefon && vendorDisplay?.telefon !== "-" ? vendorDisplay?.telefon : "-"}</span>
                  <span className="sf-contact-sep" aria-hidden="true">|</span>
                  <span><strong>Oras:</strong> {vendorDisplay?.oras && vendorDisplay?.oras !== "-" ? vendorDisplay?.oras : "-"}</span>
                </Typography>
              </Box>
            </Box>

            <Box className="sf-customer-metrics-v2">
              <Chip
                size="small"
                className="sf-customer-badge sf-customer-badge-v2"
                label={`CUI ${vendorDisplay?.cod_fiscal || "-"}`}
              />
              <Chip
                size="small"
                className="sf-customer-badge sf-customer-badge-strong sf-customer-badge-v2"
                label={`${Number(unpaidInvoicesCount || 0)} neachitate`}
              />
              <Chip
                size="small"
                label={customerTypeInfo.label}
                className={
                  customerTypeInfo.key === "SPV_ANAF"
                    ? "sf-type-chip-spv sf-type-chip-spv-v2"
                    : "sf-customer-badge sf-customer-badge-strong sf-customer-badge-v2"
                }
              />
            </Box>
          </Box>

          <Divider className="sf-side-visual-sep" />

          <Box className="sf-side-section sf-side-card sf-right-card sf-right-card-v2 sf-status-log-card">
            <Box className="sf-right-card-head">
              <Box>
                <Typography className="sf-side-title" sx={{ fontSize: "1.28rem", fontWeight: 800, letterSpacing: "0.01em" }}>
                  <CreditCardRoundedIcon fontSize="small" />
                  {selectedRow?.numar || selectedRow?.factura || selectedRow?.id || "-"}
                </Typography>
                <Typography className="sf-cell-secondary" sx={{ mt: 0.25, fontWeight: 700 }}>
                  Index incarcare: {selectedRow?.anaf_index_incarcare || selectedRow?.anaf_id_solicitare || "-"}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    icon={getTipMeta(selectedRow.tip_plata).icon}
                    label={getTipMeta(selectedRow.tip_plata).label}
                    className={getTipMeta(selectedRow.tip_plata).className}
                  />
                  <Chip
                    size="small"
                    label={selectedRow.status}
                    color={statusColor[selectedRow.status] || "default"}
                  />
                  {Number(selectedRow?.is_prediction || 0) === 1 ? (
                    <Chip
                      size="small"
                      label="PREDICTIE"
                      sx={{
                        bgcolor: "#fef3c7",
                        color: "#92400e",
                        border: "1px solid #f59e0b",
                        fontWeight: 800,
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
              <IconButton
                size="small"
                className="sf-order-edit-btn"
                color={invoiceEditMode ? "primary" : "default"}
                onClick={() => {
                  if (!invoiceEditMode) setEditInvoice(buildEditInvoiceFromRow(selectedRow));
                  setInvoiceEditMode((v) => !v);
                }}
              >
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography className="sf-side-line" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthRoundedIcon fontSize="small" />
              Data emiterii: <strong>{getInvoiceIssueDate(selectedRow.data_emitere) || "-"}</strong>
            </Typography>
            {invoiceEditMode ? (
              <>
                <Box className="sf-invoice-meta">
                  <Typography className="sf-due-date" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.28rem", fontWeight: 800, letterSpacing: "0.01em" }}>
                    <EventRoundedIcon fontSize="inherit" />
                    <span>Scadenta {dueInfo.dateLabel}</span>
                    <span style={{ color: dueInfo.color, fontWeight: 800 }}>
                      {dueInfo.deltaLabel}
                    </span>
                  </Typography>
                </Box>

                <Box className="sf-amount-grid">
                  <Box className="sf-amount-card">
                    <Typography className="sf-amount-label">
                      <PaidRoundedIcon fontSize="inherit" /> Total RON
                    </Typography>
                    <Typography className="sf-amount-value">
                      {Number(selectedRow.suma_ron || 0).toFixed(2)}
                    </Typography>
                    {foreignCurrencyInfo ? (
                      <Box className="sf-amount-fx-meta">
                        <Typography>{foreignCurrencyInfo.totalForeign}</Typography>
                        <Typography>{foreignCurrencyInfo.rateLabel}</Typography>
                      </Box>
                    ) : null}
                  </Box>
                  <Box className="sf-amount-card sf-amount-card-rest">
                    <Typography className="sf-amount-label">
                      <PaidRoundedIcon fontSize="inherit" /> Rest RON
                    </Typography>
                    <Typography className="sf-amount-value">
                      {Number(selectedRow.rest_plata_ron || 0).toFixed(2)}
                    </Typography>
                    {foreignCurrencyInfo ? (
                      <Box className="sf-amount-fx-meta">
                        <Typography>{foreignCurrencyInfo.restForeign}</Typography>
                        <Typography>{foreignCurrencyInfo.rateLabel}</Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                <Box className="sf-invoice-edit-grid">
                  <TextField
                    select
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Tip plata"
                    value={editInvoice?.tip_plata || "ONE_TIME"}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, tip_plata: e.target.value }))}
                  >
                    <MenuItem value="ONE_TIME">ONE_TIME</MenuItem>
                    <MenuItem value="RECURRENT">RECURRENT</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Nr factura"
                    value={editInvoice?.numar || ""}
                    onChange={(e) =>
                      setEditInvoice((s) => ({
                        ...s,
                        numar: e.target.value,
                        factura: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Serviciu"
                    value={editInvoice?.serviciu || ""}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, serviciu: e.target.value }))}
                  />
                  <Autocomplete
                    multiple
                    freeSolo
                    disabled={!invoiceEditMode}
                    options={RO_ACCOUNTING_OPTIONS}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return getAccountingLabel(option.split(" - ")[0] || option);
                      return option?.label || "";
                    }}
                    value={editInvoice?.cont_contabil_tags || []}
                    onChange={(_, value) =>
                      setEditInvoice((s) => ({
                        ...s,
                        cont_contabil_tags: (value || [])
                          .map((v) => {
                            if (typeof v === "string") return String(v).split(" - ")[0].trim();
                            return String(v?.code || "").trim();
                          })
                          .filter(Boolean),
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Cont contabil (taguri)"
                        placeholder="Adauga cont..."
                      />
                    )}
                  />
                  <TextField
                    type="month"
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Luna emiterii"
                    InputLabelProps={{ shrink: true }}
                    value={editInvoice?.luna || ""}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, luna: e.target.value }))}
                  />
                  <TextField
                    type="date"
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Data emitere"
                    InputLabelProps={{ shrink: true }}
                    value={editInvoice?.data_emitere || ""}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, data_emitere: e.target.value }))}
                  />
                  <TextField
                    type="date"
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Scadenta"
                    InputLabelProps={{ shrink: true }}
                    value={editInvoice?.termen_scadent || ""}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, termen_scadent: e.target.value }))}
                  />
                  <TextField
                    type="number"
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Saptamana scadentei"
                    value={editInvoice?.saptamana_scadenta || ""}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, saptamana_scadenta: e.target.value }))}
                  />
                  <TextField
                    select
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Moneda"
                    value={editInvoice?.valuta || "RON"}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, valuta: e.target.value }))}
                  >
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="RON">RON</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </TextField>
                  <TextField
                    type="number"
                    size="small"
                    disabled={!invoiceEditMode}
                    label="Suma EUR/USD"
                    value={editInvoice?.suma_valuta ?? ""}
                    onChange={(e) => setEditInvoice((s) => ({ ...s, suma_valuta: e.target.value }))}
                  />
                  <TextField
                    size="small"
                    label="Curs BNR instant"
                    value={
                      fxPreview?.cursBnr
                        ? `${fxPreview.cursBnr} (${fxPreview.cursDate || "-"})`
                        : "N/A (RON)"
                    }
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    size="small"
                    label="RON instant"
                    value={Number(fxPreview?.sumaRon || 0).toFixed(2)}
                    InputProps={{ readOnly: true }}
                  />
                  {fxPreviewLoading ? <CircularProgress size={16} /> : null}
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={!invoiceEditMode}
                        checked={Boolean(editInvoice?.transmisa_in_spv)}
                        onChange={(e) =>
                          setEditInvoice((s) => ({ ...s, transmisa_in_spv: e.target.checked }))
                        }
                      />
                    }
                    label="Transmisa in SPV"
                  />
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 3,
                  border: "1px solid rgba(59,130,246,0.22)",
                  background:
                    "radial-gradient(120% 120% at 0% 0%, rgba(219,234,254,0.9) 0%, rgba(248,250,252,0.96) 42%, rgba(241,245,249,0.98) 100%)",
                  boxShadow: "0 14px 30px rgba(2,6,23,0.08)",
                  p: 1.2,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  <Box sx={{ p: 1, borderRadius: 2, background: "#ffffff", border: "1px solid #dbeafe", boxShadow: "0 4px 10px rgba(30,64,175,0.08)" }}>
                    <Stack direction="row" spacing={0.9} alignItems="center">
                      <Box sx={{ width: 24, height: 24, borderRadius: "8px", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#2563eb,#06b6d4)", color: "#fff" }}>
                        <CalendarMonthRoundedIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#475569" }}>
                        Scadenta
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.45, fontSize: 15, fontWeight: 900, color: "#0f172a" }}>
                      {dueInfo.dateLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1, borderRadius: 2, background: "#ffffff", border: "1px solid #fee2e2", boxShadow: "0 4px 10px rgba(220,38,38,0.08)" }}>
                    <Stack direction="row" spacing={0.9} alignItems="center">
                      <Box sx={{ width: 24, height: 24, borderRadius: "8px", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff" }}>
                        <HourglassTopRoundedIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#475569" }}>
                        Termen
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.45, fontSize: 15, fontWeight: 900, color: dueInfo.color }}>
                      {dueInfo.deltaLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ p: 1, borderRadius: 2, background: "#ffffff", border: "1px solid #dcfce7", boxShadow: "0 4px 10px rgba(22,163,74,0.08)" }}>
                    <Stack direction="row" spacing={0.9} alignItems="center">
                      <Box sx={{ width: 24, height: 24, borderRadius: "8px", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#16a34a,#22c55e)", color: "#fff" }}>
                        <AccountBalanceWalletRoundedIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#475569" }}>
                        Total RON
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.45, fontSize: 15, fontWeight: 900, color: "#0f172a" }}>
                      {Number(selectedRow.suma_ron || 0).toFixed(2)}
                    </Typography>
                    {foreignCurrencyInfo ? (
                      <Box className="sf-amount-fx-meta">
                        <Typography>{foreignCurrencyInfo.totalForeign}</Typography>
                        <Typography>{foreignCurrencyInfo.rateLabel}</Typography>
                      </Box>
                    ) : null}
                  </Box>

                  <Box sx={{ p: 1, borderRadius: 2, background: "#ffffff", border: "1px solid #fde68a", boxShadow: "0 4px 10px rgba(202,138,4,0.08)" }}>
                    <Stack direction="row" spacing={0.9} alignItems="center">
                      <Box sx={{ width: 24, height: 24, borderRadius: "8px", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#eab308,#f97316)", color: "#fff" }}>
                        <PriceCheckRoundedIcon sx={{ fontSize: 15 }} />
                      </Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#475569" }}>
                        Rest RON
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.45, fontSize: 15, fontWeight: 900, color: "#0f172a" }}>
                      {Number(selectedRow.rest_plata_ron || 0).toFixed(2)}
                    </Typography>
                    {foreignCurrencyInfo ? (
                      <Box className="sf-amount-fx-meta">
                        <Typography>{foreignCurrencyInfo.restForeign}</Typography>
                        <Typography>{foreignCurrencyInfo.rateLabel}</Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 2,
                    border: "1px dashed rgba(59,130,246,0.35)",
                    background: "linear-gradient(120deg, rgba(239,246,255,0.9), rgba(224,242,254,0.75))",
                  }}
                >
                  <Stack direction="row" spacing={0.9} alignItems="center">
                    <Box sx={{ width: 24, height: 24, borderRadius: "8px", display: "grid", placeItems: "center", background: "linear-gradient(135deg,#312e81,#1d4ed8)", color: "#fff" }}>
                      <FingerprintRoundedIcon sx={{ fontSize: 15 }} />
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#334155" }}>
                      Index incarcare
                    </Typography>
                  </Stack>
                  <Typography sx={{ mt: 0.45, fontSize: 13, fontWeight: 800, color: "#0f172a", wordBreak: "break-all" }}>
                    {selectedRow?.anaf_index_incarcare || selectedRow?.anaf_id_solicitare || "-"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box className="sf-panel-save-wrap">
              {invoiceEditMode ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    className="sf-panel-save-btn"
                    onClick={() => {
                      setEditInvoice(buildEditInvoiceFromRow(selectedRow));
                      setInvoiceEditMode(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    className="sf-btn-primary sf-panel-save-btn"
                    disabled={invoiceSaving || !editInvoice}
                    onClick={async () => {
                      if (!selectedRow?.id || !onSaveInvoice || !editInvoice) return;
                      try {
                        setInvoiceSaving(true);
                        setPanelError("");
                        const saved = await onSaveInvoice(selectedRow, {
                          ...editInvoice,
                          cont_contabil: (editInvoice.cont_contabil_tags || []).join(", "),
                          transmisa_in_spv: editInvoice.transmisa_in_spv ? 1 : 0,
                          suma_valuta: Number(editInvoice.suma_valuta || 0),
                          saptamana_scadenta: Number(editInvoice.saptamana_scadenta || 0),
                        });
                        if (saved) {
                          if (editInvoice?.tip_plata && selectedRow?.vendor_id) {
                            onPatchVendorInvoices?.(selectedRow.vendor_id, {
                              tip_plata: editInvoice.tip_plata,
                            });
                          }
                          setSelectedRow((prev) => ({ ...prev, ...saved }));
                          onPatchInvoice?.(saved);
                        }
                        setInvoiceEditMode(false);
                      } catch (err) {
                        setPanelError(err?.response?.data?.error || err.message || "Nu am putut salva factura.");
                      } finally {
                        setInvoiceSaving(false);
                      }
                    }}
                  >
                    {invoiceSaving ? <CircularProgress size={16} color="inherit" /> : "Save"}
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </Box>

          <Divider sx={{ my: 1.25 }} />

          <Box className="sf-side-section sf-side-card sf-right-card sf-right-card-v2">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography className="sf-side-title">Linii factura (produse/servicii)</Typography>
              <Chip
                size="small"
                icon={<ReceiptLongRoundedIcon />}
                label={`${selectedInvoiceLines.length} linii`}
                className="sf-pay-count-chip"
              />
            </Stack>
            {!selectedInvoiceLines.length ? (
              <Typography className="sf-cell-secondary">Nu exista linii extrase din XML pentru aceasta factura.</Typography>
            ) : (
              <Box sx={{ overflowX: "auto", mt: 1 }}>
                <Table size="small" className="sf-lines-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Descriere</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell align="right">Pret</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">TVA %</TableCell>
                      <TableCell align="right">Baza TVA</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Charge</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoiceLines.map((line) => (
                      <TableRow key={line.id || `${line.invoice_id}-${line.line_index}`}>
                        <TableCell>{line.line_index || "-"}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{line.item_name || "-"}</Typography>
                          {line.item_description ? (
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>{line.item_description}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{line.quantity ?? "-"}</TableCell>
                        <TableCell align="right">{line.unit_price ?? "-"}</TableCell>
                        <TableCell align="right">{line.line_extension_amount ?? "-"}</TableCell>
                        <TableCell align="right">{line.tax_percent ?? "-"}</TableCell>
                        <TableCell align="right">{line.tax_subtotal_amount ?? "-"}</TableCell>
                        <TableCell align="right">{line.allowance_total_amount ?? "-"}</TableCell>
                        <TableCell align="right">{line.charge_total_amount ?? "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1.25 }} />

          <Box className="sf-side-section sf-side-card sf-right-card sf-right-card-v2">
            <Box className="sf-right-card-head">
              <Typography className="sf-side-title">
                <PictureAsPdfRoundedIcon fontSize="small" />
                Document dovada factura
              </Typography>
              {invoiceProofUrl ? (
                <Chip size="small" color="success" label="Dovada existenta" />
              ) : (
                <Chip size="small" color="warning" label="Dovada lipsa" />
              )}
            </Box>

            <input
              ref={invoiceDocInputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                applyInvoiceDocCandidate(file);
              }}
            />

            <Box
              className={`sf-doc-dropzone ${invoiceDropActive ? "is-active" : ""}`}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setInvoiceDropActive(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setInvoiceDropActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setInvoiceDropActive(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setInvoiceDropActive(false);
                const file = e.dataTransfer?.files?.[0] || null;
                applyInvoiceDocCandidate(file);
              }}
              onClick={() => invoiceDocInputRef.current?.click()}
            >
              <PictureAsPdfRoundedIcon className="sf-doc-dropzone-icon" />
              <Typography className="sf-doc-dropzone-title">Drag & drop PDF/JPG/PNG</Typography>
              <Typography className="sf-doc-dropzone-sub">sau click pentru a selecta fisierul</Typography>
            </Box>

            <Stack direction="row" spacing={1} className="sf-doc-actions-row">
              {isSpvSource ? (
                <Button
                  variant="outlined"
                  size="small"
                  className="sf-doc-xml-btn"
                  startIcon={
                    invoiceDocSaving ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AutoAwesomeRoundedIcon fontSize="small" />
                    )
                  }
                  onClick={async () => {
                    if (!onGenerateFromSpv) {
                      setPanelError("Generate XML indisponibil: callback lipsa in frontend.");
                      console.error("[SF][frontend][generate-xml] missing onGenerateFromSpv callback");
                      return;
                    }
                    if (!selectedRow?.external_id) {
                      setPanelError("Nu exista external_id pe factura selectata, nu pot genera din XML.");
                      console.warn("[SF][frontend][generate-xml] missing external_id", {
                        invoice_id: selectedRow?.id || null,
                        source: selectedRow?.source || null,
                        factura: selectedRow?.factura || null,
                        numar: selectedRow?.numar || null,
                      });
                      return;
                    }
                    try {
                      console.info("[SF][frontend][generate-xml] click", {
                        invoice_id: selectedRow?.id || null,
                        external_id: selectedRow?.external_id || null,
                        source: selectedRow?.source || null,
                      });
                      setInvoiceDocSaving(true);
                      setPanelError("");
                      setPanelInfo("");
                      const result = await onGenerateFromSpv(selectedRow);
                      const patched = result?.invoice || result?.data || null;
                      if (patched) {
                        setSelectedRow((prev) => ({ ...prev, ...patched }));
                        onPatchInvoice?.(patched);
                      }
                      if (onRefreshQuiet) {
                        await onRefreshQuiet();
                      }
                      const action = String(result?.action || "").toLowerCase();
                      if (action === "already_generated") {
                        setPanelInfo("Factura era deja generata din XML pentru acest document.");
                      } else if (action === "updated" || action === "inserted") {
                        setPanelInfo(`Factura a fost generata din XML (${action}).`);
                      } else {
                        setPanelInfo("Generare XML finalizata.");
                      }
                    } catch (err) {
                      setPanelError(err?.response?.data?.error || err?.message || "Nu am putut genera factura din XML SPV.");
                    } finally {
                      setInvoiceDocSaving(false);
                    }
                  }}
                  disabled={invoiceDocSaving || !selectedRow?.external_id}
                >
                  Genereaza XML
                </Button>
              ) : null}

              <Button
                variant="contained"
                size="small"
                className="sf-doc-save-btn"
                startIcon={
                  invoiceDocSaving ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SaveRoundedIcon fontSize="small" />
                  )
                }
                disabled={invoiceDocSaving || !invoiceDocFile || !onUpload || !onSaveInvoice}
                onClick={async () => {
                  if (!selectedRow?.id || !invoiceDocFile) return;
                  try {
                    setInvoiceDocSaving(true);
                    setInvoiceDocError("");
                    setPanelError("");
                    const emitere = String(editInvoice?.data_emitere || selectedRow?.data_emitere || new Date().toISOString().slice(0, 10));
                    const d = new Date(emitere);
                    const yyyy = Number.isFinite(d.getTime()) ? String(d.getFullYear()) : String(new Date().getFullYear());
                    const mm = Number.isFinite(d.getTime()) ? String(d.getMonth() + 1).padStart(2, "0") : String(new Date().getMonth() + 1).padStart(2, "0");
                    const s3Prefix = `factura/${yyyy}/${mm}`;

                    if (onValidateInvoicePdf) {
                      try {
                        const validation = await onValidateInvoicePdf(selectedRow, invoiceDocFile);
                        const warns = Array.isArray(validation?.warnings) ? validation.warnings : [];
                        if (warns.length) {
                          setInvoiceDocWarning(warns.join(" | "));
                        } else {
                          setInvoiceDocWarning("");
                        }
                      } catch (validationErr) {
                        setInvoiceDocWarning(validationErr?.response?.data?.error || validationErr?.message || "Nu am putut valida PDF cu AI.");
                      }
                    }

                    const uploaded = await onUpload(invoiceDocFile, {
                      s3_prefix: s3Prefix,
                      folder: s3Prefix,
                      invoice_id: selectedRow?.id,
                      vendor_id: selectedRow?.vendor_id,
                      source_module: "scadentar-furnizori",
                    });
                    const url =
                      uploaded?.doc_factura_url ||
                      uploaded?.doc_plata_url ||
                      uploaded?.url ||
                      uploaded?.location ||
                      uploaded?.fileUrl ||
                      null;
                    if (!url) throw new Error("Upload realizat, dar fara URL.");
                    const saved = await onSaveInvoice(selectedRow, {
                      doc_factura_url: url,
                      doc_plata_url: url,
                    });
                    if (saved) {
                      setSelectedRow((prev) => ({ ...prev, ...saved }));
                      onPatchInvoice?.(saved);
                    } else {
                      const fallbackPatch = { id: selectedRow.id, doc_factura_url: url, doc_plata_url: url };
                      setSelectedRow((prev) => ({ ...prev, ...fallbackPatch }));
                      onPatchInvoice?.(fallbackPatch);
                    }
                    setInvoiceDocFile(null);
                    if (invoiceDocInputRef.current) invoiceDocInputRef.current.value = "";
                  } catch (err) {
                    setInvoiceDocError(err?.response?.data?.error || err?.message || "Nu am putut salva dovada.");
                  } finally {
                    setInvoiceDocSaving(false);
                  }
                }}
              >
                Salveaza dovada
              </Button>
            </Stack>

            {invoiceDocFile ? (
              <Box className="sf-upload-pill-wrap">
                <Chip
                  icon={<PictureAsPdfRoundedIcon />}
                  label={invoiceDocFile.name}
                  onDelete={() => {
                    setInvoiceDocFile(null);
                    if (invoiceDocInputRef.current) invoiceDocInputRef.current.value = "";
                  }}
                  className="sf-upload-pill"
                />
              </Box>
            ) : null}

            {invoiceProofProxyUrl ? (
              <Tooltip title="Deschide dovada curenta">
                <IconButton
                  className="sf-proof-pdf-btn"
                  onClick={() => window.open(invoiceProofProxyUrl, "_blank", "noopener,noreferrer")}
                >
                  <PictureAsPdfRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : null}

            {invoiceDocError ? <Typography className="sf-side-error">{invoiceDocError}</Typography> : null}
            {invoiceDocWarning ? <Alert severity="warning">{invoiceDocWarning}</Alert> : null}
          </Box>

          <Divider sx={{ my: 1.25 }} />

          <Box className="sf-side-section sf-side-card sf-payment-card">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography className="sf-side-title">Plati</Typography>
              <Chip
                size="small"
                icon={<ReceiptLongRoundedIcon />}
                label={`${selectedPayments.length} inregistrari`}
                className="sf-pay-count-chip"
              />
            </Stack>
            <PaymentEntryForm
              selectedRow={selectedRow}
              onQuickPayment={onQuickPayment}
              onPatchInvoice={onPatchInvoice}
              setSelectedRow={setSelectedRow}
              setSelectedPayments={setSelectedPayments}
              setPanelError={setPanelError}
            />
            {!selectedPayments.length ? (
              <Typography className="sf-cell-secondary">Nu exista plati inregistrate.</Typography>
            ) : (
              <Box className="sf-side-list sf-payments-timeline">
                {selectedPayments.map((p) => (
                  <Box
                    key={p.id}
                    className={`sf-side-item sf-payment-timeline-item ${paymentMethodClass(p.payment_method)}`}
                  >
                    <Box className="sf-payment-method-avatar">
                      {paymentMethodIcon(p.payment_method)}
                    </Box>
                    <Box className="sf-payment-timeline-content">
                      <Box className="sf-payment-row-main">
                        <Box className="sf-payment-title-wrap">
                          <Typography className="sf-payment-amount">
                            {Number(p.amount_ron || 0).toFixed(2)} RON
                          </Typography>
                          <Typography className="sf-payment-date">
                            <EventRoundedIcon fontSize="inherit" />
                            {formatPaymentDate(p.payment_date)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={paymentMethodLabel(p.payment_method)}
                          className={`sf-payment-method-chip ${paymentMethodClass(p.payment_method)}`}
                        />
                      </Box>
                      <Box className={`sf-payment-evidence-card ${p.op_number ? "" : "is-empty"}`}>
                        <FingerprintRoundedIcon fontSize="small" />
                        <span className="sf-payment-evidence-label">Evidenta plata</span>
                        <span className="sf-payment-evidence-value">
                          {p.op_number || "Necompletata"}
                        </span>
                      </Box>
                      {p.doc_plata_url ? (
                        <Link
                          href={p.doc_plata_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sf-payment-doc-link"
                        >
                          <PictureAsPdfRoundedIcon fontSize="small" />
                          Deschide PDF
                        </Link>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box className="sf-side-section sf-side-card sf-right-card sf-right-card-v2">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography className="sf-side-title">Manual status log</Typography>
              <Chip size="small" label={`${statusAudit.length} schimbari`} className="sf-pay-count-chip" />
            </Stack>
            {!statusAudit.length ? (
              <Typography className="sf-cell-secondary sf-status-empty-text">Nu exista schimbari manuale inregistrate.</Typography>
            ) : (
              <Box className="sf-side-list sf-status-audit-list">
                {statusAudit.map((item) => (
                  <Box key={item.id} className="sf-side-item sf-status-audit-item">
                    <Typography className="sf-side-line sf-status-audit-line">
                      <strong>{item.changed_by || "manual-ui"}</strong>
                      <span>{formatAuditDateTime(item.changed_at)}</span>
                    </Typography>
                    <Typography className="sf-cell-secondary">
                      {buildManualStatusMeta(item.from_status, item.to_status)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Stack className="sf-invoice-delete-wrap" direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              className="sf-invoice-delete-btn"
              startIcon={
                invoiceDeleting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteRoundedIcon fontSize="small" />
                )
              }
              disabled={invoiceDeleting || !selectedRow?.id || !onDeleteInvoice}
              onClick={async () => {
                if (!selectedRow?.id || !onDeleteInvoice) return;
                const label = selectedRow.factura || selectedRow.numar || selectedRow.id;
                if (!window.confirm(`Stergi factura ${label}?`)) return;
                try {
                  setInvoiceDeleting(true);
                  setPanelError("");
                  await onDeleteInvoice(selectedRow);
                  setSelectedRow(null);
                  onSelectInvoice?.(null);
                  setPanelInfo("");
                } catch (err) {
                  setPanelError(err?.response?.data?.error || err?.message || "Nu am putut sterge factura.");
                } finally {
                  setInvoiceDeleting(false);
                }
              }}
            >
              Sterge factura
            </Button>
          </Stack>

          {panelInfo ? <Alert severity="success" sx={{ mt: 1 }}>{panelInfo}</Alert> : null}
          {panelError ? <Typography className="sf-side-error">{panelError}</Typography> : null}

          {statusConfirmDialog}
          </Paper>
        </Box>
      )}
      {!selectedRow ? statusConfirmDialog : null}
    </Box>
  );
}

export default OverviewTab;
